import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/feature/SEO';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';

const testimonials = [
  {
    name: '李思远',
    role: '互联网创业者',
    content: '接触一本道的理念后，我开始聚焦核心业务，砍掉了三个副业项目。半年下来，主营业务收入反而增长了60%。专一的力量远超想象。',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20confident%20Chinese%20man%20in%20his%2030s%20with%20warm%20genuine%20smile%20wearing%20casual%20blazer%20soft%20studio%20lighting%20cream%20background&width=100&height=100&seq=testimonial-01&orientation=squarish',
  },
  {
    name: '王静怡',
    role: '自由设计师',
    content: '以前总觉得同时接多个项目才能赚更多钱。按照一本道的方法专注一个方向深耕后，不仅作品质量大幅提升，客户也愿意支付更高的费用。',
    avatar: 'https://readdy.ai/api/search-image?query=Warm%20portrait%20of%20a%20creative%20Chinese%20woman%20with%20gentle%20eyes%20and%20a%20natural%20confident%20expression%20wearing%20simple%20earth-toned%20clothing%20soft%20natural%20light&width=100&height=100&seq=testimonial-02&orientation=squarish',
  },
  {
    name: '张明远',
    role: '企业管理者',
    content: '作为管理者，我最大的改变是在团队中推行"聚焦一件事"的文化。减少了无意义的会议和项目切换后，团队整体效率提升了40%以上。',
    avatar: 'https://readdy.ai/api/search-image?query=Mature%20Chinese%20man%20in%20his%2040s%20with%20kind%20authoritative%20presence%20wearing%20simple%20white%20shirt%20warm%20natural%20window%20light%20minimalist%20background&width=100&height=100&seq=testimonial-03&orientation=squarish',
  },
  {
    name: '陈雨萱',
    role: '研究生 / 内容创作者',
    content: '一本道的"走稳每一步"理念帮我度过了论文写作最焦虑的时期。不再盯着最终的几万字，而是每天踏踏实实写500字，三个月后论文初稿就完成了。',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20woman%20with%20bright%20thoughtful%20expression%20wearing%20round%20glasses%20and%20a%20simple%20sweater%20soft%20daylight%20from%20a%20windowside%20minimalist%20cream%20background&width=100&height=100&seq=testimonial-04&orientation=squarish',
  },
  {
    name: '赵恒',
    role: '投资分析师',
    content: '在投资领域，专注和纪律是制胜关键。一本道的"心无旁骛"理念帮助我过滤掉大量噪音，只关注少数真正优质的标的，投资回报率显著提升。',
    avatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20sharp%20focused%20Chinese%20man%20in%20his%2030s%20with%20clean%20professional%20look%20wearing%20a%20dark%20polo%20shirt%20soft%20studio%20lighting%20minimalist%20background&width=100&height=100&seq=testimonial-05&orientation=squarish',
  },
  {
    name: '林婉儿',
    role: '瑜伽导师 / 正念教练',
    content: '一本道的理念和正念冥想的精髓完全相通。我经常在课程中引用一本道的"专一之道"来帮助学员建立身心合一的状态，效果出奇地好。',
    avatar: 'https://readdy.ai/api/search-image?query=Serene%20portrait%20of%20a%20gentle%20Chinese%20woman%20with%20a%20calm%20meditative%20presence%20wearing%20natural%20linen%20clothing%20soft%20diffused%20natural%20light%20zen%20minimalist%20background&width=100&height=100&seq=testimonial-06&orientation=squarish',
  },
];

const faqs = [
  {
    q: '什么是一本道的核心理念？',
    a: '一本道的核心理念是"心无旁骛、专注目标、走稳每一步"。我们倡导在纷繁复杂的世界中，选择一个方向，以专一之心持续深耕，不急不躁，扎扎实实地走好人生每一步。',
  },
  {
    q: '一本道适合什么样的人？',
    a: '一本道适合所有追求个人成长、渴望提升专注力和执行力的人。无论你是职场人士、创业者、自由职业者还是学生，只要你有心在某个方向上持续精进，一本道的理念都能为你提供指导。',
  },
  {
    q: '如何开始践行一本道？',
    a: '我们建议从三个步骤开始：第一，明确你的核心方向——选择一件你愿意长期投入的事情；第二，建立日课习惯——每天为这个方向做一点积累；第三，定期反思调整——每周花一点时间审视自己的进展和方向。',
  },
  {
    q: '一本道的内容多久更新一次？',
    a: '我们保持每周2-3篇高质量原创文章的更新频率，涵盖专注之道、目标管理、人生哲学和心路历程四大板块。每一篇文章都经过深度思考和精心打磨。',
  },
  {
    q: '我可以投稿给一本道吗？',
    a: '非常欢迎！如果你有与专注、成长、人生哲学相关的真实经历和深度思考，欢迎通过本页的联系表单与我们取得联系。我们珍视每一份真诚的分享。',
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qabm4nlsngrm4lluhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([k]) => k !== 'phone_alt')
            .map(([k, v]) => [k, typeof v === 'string' ? v : ''])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络异常，请检查网络后重试。');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '一本道',
    url: 'https://yibendao.cn',
    description: '一本道，专注于正道、专一、坚定前行的人生成长理念。',
    foundingDate: '2025',
    inLanguage: 'zh-CN',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于我们 - 一本道"
        description="了解一本道的品牌故事、核心理念与团队愿景。一本道专注于正道、专一、坚定前行的人生成长理念，帮助每一个人走稳人生每一步。"
        keywords="一本道,关于一本道,一本道品牌介绍,一本道理念,一本道团队,一本道联系方式"
        canonicalUrl="https://yibendao.cn/about"
        ogType="website"
      />
      <SchemaOrg schema={orgSchema} />
      <SchemaOrg schema={faqSchema} />

      <Navbar />
      <main className="pt-20 pb-16">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="relative w-full h-[320px] md:h-[400px] rounded-lg overflow-hidden mt-6 mb-14">
            <img
              src="https://readdy.ai/api/search-image?query=A%20serene%20traditional%20Chinese%20study%20room%20with%20warm%20golden%20sunlight%20streaming%20through%20wooden%20lattice%20windows%20an%20ancient%20scroll%20and%20ink%20stone%20on%20a%20dark%20wooden%20desk%20incense%20smoke%20curling%20gently%20minimalist%20zen%20atmosphere%20rich%20textures%20high%20detail&width=1600&height=800&seq=about-hero-01&orientation=landscape"
              alt="一本道 · 关于我们"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-background-50 drop-shadow-md">
                关于一本道
              </h1>
              <p className="mt-2 text-sm text-background-200 max-w-lg leading-relaxed">
                坚守一条正道，走稳每一步人生路
              </p>
            </div>
          </div>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
              品牌故事
            </h2>
            <div className="prose-custom text-foreground-700 leading-relaxed space-y-4">
              <p>
                <strong>一本道</strong>诞生于一个简单而深刻的洞察：在这个信息爆炸、机会泛滥的时代，人们最稀缺的不是能力、不是资源，而是<strong>专一的定力</strong>。
              </p>
              <p>
                我们看到太多优秀的人，因为精力过于分散而未能达到本应达到的高度；我们看到太多美好的梦想，因为缺乏持续的专注而中途夭折。这个时代给予我们前所未有的选择自由，却也让我们前所未有地容易迷失方向。
              </p>
              <p>
                "一"为专一——在万千方向中选择一条，坚定不移地走下去；"本"为根本——守住内心的本质追求，不被外界的浮华所动摇；"道"为道路——找到属于自己的那条正道，一步一步踏实地前行。
              </p>
              <p>
                一本道不追求速度，而追求深度；不追求广度，而追求精度。我们相信，真正的成长从来不是冲刺，而是一场每一步都走得扎实的马拉松。当你把全部的精力聚焦在一个方向上，时间的复利会给你最慷慨的回报。
              </p>
              <p>
                自2025年创立以来，一本道已经汇集了数万名志同道合的践行者。我们通过深度文章、社区交流和理念传播，帮助越来越多的人在纷繁世界中找到自己的"一"，并以专一之心持续精进。
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
              我们的使命
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-compass-3-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">指引方向</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  帮助每一位追寻者在迷茫中找到属于自己的正道，建立清晰的人生方向和价值坐标。
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-heart-pulse-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">赋能成长</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  提供系统化的方法论和真实的成长故事，赋能每个人走稳自己的每一步，实现持续精进。
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-group-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">凝聚同道</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  汇聚志同道合者，建立一个互相激励、共同成长的社区，让专一之道不再是一个人的修行。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-8">
              同道口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="bg-background-100 rounded-lg p-6 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    "{t.content}"
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-8">
              常见问题
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-background-100 rounded-lg border border-background-200/70 group"
                >
                  <summary className="px-6 py-4 cursor-pointer flex items-center justify-between font-medium text-foreground-800 text-sm hover:text-primary-600 transition-colors duration-200">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                    <p className="text-sm text-foreground-500">contact@yibendao.cn</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-map-pin-line text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">所在城市</p>
                    <p className="text-sm text-foreground-500">中国 · 杭州</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-time-line text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">运营理念</p>
                    <p className="text-sm text-foreground-500">心无旁骛 · 专注目标 · 走稳每一步</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-background-100 rounded-lg p-6 border border-background-200/70 space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      留言 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <div className="flex items-center justify-between">
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? (
                        <>发送中...</>
                      ) : (
                        <>
                          发送留言
                          <i className="ri-send-plane-line text-sm"></i>
                        </>
                      )}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-secondary-600 font-medium">留言已发送，我们会尽快回复！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}